import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { FileText, Clock, CheckCircle, XCircle, Filter } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PageHeader } from '../components/dashboard/PageHeader';
import { StatCard } from '../components/dashboard/StatCard';
import { AttachmentList } from '../components/tickets/ContractingFields';

const API = process.env.REACT_APP_BACKEND_URL;

// Session is carried by the httpOnly auth cookie (axios withCredentials)
const getAuthHeader = () => ({});

const statusColors = {
  SUBMITTED: 'bg-blue-100 text-blue-700',
  UNDER_REVIEW: 'bg-amber-100 text-amber-700',
  APPROVED: 'bg-green-100 text-green-700',
  DENIED: 'bg-red-100 text-red-700',
  RESOLVED: 'bg-emerald-100 text-emerald-700'
};

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  const [actionStatus, setActionStatus] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [finalCommission, setFinalCommission] = useState('');
  const [finalUplineId, setFinalUplineId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    let filtered = tickets;
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }
    if (typeFilter !== 'all') {
      filtered = filtered.filter(t => t.ticket_type === typeFilter);
    }
    setFilteredTickets(filtered);
  }, [tickets, statusFilter, typeFilter]);

  const fetchTickets = async () => {
    try {
      console.log('Fetching tickets from:', `${API}/api/tickets`);
      const response = await axios.get(`${API}/api/tickets`, getAuthHeader());
      console.log('Tickets response:', response.data);
      setTickets(response.data);
      setFilteredTickets(response.data);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const viewDetail = (ticket) => {
    setSelectedTicket(ticket);
    setActionStatus('');
    setResolutionNotes('');
    setFinalCommission(ticket.requested_commission_level || '');
    setFinalUplineId(ticket.requested_upline_id || '');
    setDetailOpen(true);
  };

  const handleAction = async () => {
    if (!actionStatus) {
      toast.error('Please select an action');
      return;
    }
    
    if (['DENIED', 'RESOLVED'].includes(actionStatus) && !resolutionNotes) {
      toast.error('Resolution notes required');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/api/tickets/${selectedTicket.id}/status`, {
        status: actionStatus,
        admin_resolution_notes: resolutionNotes,
        final_commission_level: finalCommission ? parseFloat(finalCommission) : null,
        final_upline_id: finalUplineId || null
      }, getAuthHeader());

      toast.success(`Ticket ${actionStatus.toLowerCase()}`);
      setDetailOpen(false);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getTicketTypeLabel = (type) => {
    const labels = {
      COMMISSION_CHANGE: 'Commission Change',
      HIERARCHY_MOVE: 'Hierarchy Move',
      REINSTATEMENT: 'Reinstatement',
      CONTRACTING: 'Contracting'
    };
    return labels[type] || type;
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        title="Tickets Management"
        subtitle="Admin-only governance workflow"
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard title="Total" value={tickets.length} icon={FileText} color="blue" index={0} testId="admin-tickets-stat-total" />
        <StatCard title="Submitted" value={tickets.filter(t => t.status === 'SUBMITTED').length} icon={Clock} color="purple" index={1} testId="admin-tickets-stat-submitted" />
        <StatCard title="Under Review" value={tickets.filter(t => t.status === 'UNDER_REVIEW').length} icon={Clock} color="orange" index={2} testId="admin-tickets-stat-review" />
        <StatCard title="Resolved" value={tickets.filter(t => t.status === 'RESOLVED').length} icon={CheckCircle} color="green" index={3} testId="admin-tickets-stat-resolved" />
        <StatCard title="Denied" value={tickets.filter(t => t.status === 'DENIED').length} icon={XCircle} color="pink" index={4} testId="admin-tickets-stat-denied" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-[#A3AED0]" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 px-3 rounded-md border border-[#E9EDF7] bg-white text-sm"
            >
              <option value="all">All Status</option>
              <option value="SUBMITTED">Submitted</option>
              <option value="UNDER_REVIEW">Under Review</option>
              <option value="APPROVED">Approved</option>
              <option value="DENIED">Denied</option>
              <option value="RESOLVED">Resolved</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="h-9 px-3 rounded-md border border-[#E9EDF7] bg-white text-sm"
            >
              <option value="all">All Types</option>
              <option value="COMMISSION_CHANGE">Commission Change</option>
              <option value="HIERARCHY_MOVE">Hierarchy Move</option>
              <option value="REINSTATEMENT">Reinstatement</option>
              <option value="CONTRACTING">Contracting</option>
            </select>
            <span className="text-sm text-[#A3AED0] ml-auto">{filteredTickets.length} tickets</span>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      {loading ? (
        <Card><CardContent className="p-8 text-center text-[#A3AED0]">Loading...</CardContent></Card>
      ) : filteredTickets.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-10 w-10 text-[#A3AED0] mx-auto mb-3" />
            <p className="text-[#A3AED0]">No tickets found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map((ticket) => (
            <motion.div key={ticket.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="hover:border-[#2447D6]/50 transition-colors cursor-pointer" onClick={() => viewDetail(ticket)}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="font-semibold">{getTicketTypeLabel(ticket.ticket_type)}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[ticket.status]}`}>
                          {ticket.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="text-sm text-[#2B3674]/80">
                        Agent: {ticket.affected_agent_name} | Submitted by: {ticket.submitted_by_agent_name}
                      </div>
                      <div className="text-xs text-[#A3AED0] mt-1">
                        {formatDate(ticket.created_at)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Detail & Action Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ticket Review & Action</DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              {/* Ticket Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-[#A3AED0] uppercase mb-1">Type</div>
                  <div className="font-medium">{getTicketTypeLabel(selectedTicket.ticket_type)}</div>
                </div>
                <div>
                  <div className="text-xs text-[#A3AED0] uppercase mb-1">Current Status</div>
                  <span className={`text-xs px-2 py-1 rounded-full ${statusColors[selectedTicket.status]}`}>
                    {selectedTicket.status.replace('_', ' ')}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-[#A3AED0] uppercase mb-1">Submitted By</div>
                  <div>{selectedTicket.submitted_by_agent_name}</div>
                </div>
                <div>
                  <div className="text-xs text-[#A3AED0] uppercase mb-1">Affected Agent</div>
                  <div>{selectedTicket.affected_agent_name}</div>
                </div>
              </div>

              {selectedTicket.requested_commission_level && (
                <div className="p-4 rounded-lg bg-[#F4F7FE]">
                  <div className="text-xs text-[#A3AED0] uppercase mb-2">Commission Change Request</div>
                  <div className="text-lg font-semibold">{selectedTicket.current_commission_level}% → {selectedTicket.requested_commission_level}%</div>
                </div>
              )}

              {selectedTicket.requested_upline_name && (
                <div className="p-4 rounded-lg bg-[#F4F7FE]">
                  <div className="text-xs text-[#A3AED0] uppercase mb-2">Hierarchy Move Request</div>
                  <div className="text-lg font-semibold">{selectedTicket.current_upline_name || 'None'} → {selectedTicket.requested_upline_name}</div>
                </div>
              )}

              {selectedTicket.contracting_issue_label && (
                <div className="p-4 rounded-lg bg-[#F4F7FE] grid grid-cols-2 gap-4" data-testid="admin-ticket-contracting">
                  <div className="col-span-2">
                    <div className="text-xs text-[#A3AED0] uppercase mb-1">Contracting Issue</div>
                    <div className="text-lg font-semibold">{selectedTicket.contracting_issue_label}</div>
                  </div>
                  {selectedTicket.carrier_name && (
                    <>
                      <div><div className="text-xs text-[#A3AED0] uppercase mb-1">Carrier</div><div className="font-medium">{selectedTicket.carrier_name}</div></div>
                      <div><div className="text-xs text-[#A3AED0] uppercase mb-1">Writing Number</div><div className="font-mono font-medium">{selectedTicket.writing_number}</div></div>
                    </>
                  )}
                </div>
              )}

              <div>
                <div className="text-xs text-[#A3AED0] uppercase mb-1">{selectedTicket.ticket_type === 'CONTRACTING' ? 'Comments' : 'Reason'}</div>
                <div className="text-sm whitespace-pre-wrap p-3 rounded-lg bg-[#F4F7FE]">{selectedTicket.reason_text}</div>
              </div>

              <AttachmentList attachments={selectedTicket.attachments} />

              {/* Admin Actions */}
              {selectedTicket.status !== 'RESOLVED' && selectedTicket.status !== 'DENIED' && (
                <div className="border-t pt-4 space-y-4">
                  <h3 className="font-semibold">Admin Action</h3>
                  
                  <div className="space-y-1">
                    <Label>Action *</Label>
                    <select
                      value={actionStatus}
                      onChange={(e) => setActionStatus(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-[#E9EDF7] bg-white"
                    >
                      <option value="">Select Action</option>
                      <option value="UNDER_REVIEW">Mark Under Review</option>
                      <option value="APPROVED">Approve</option>
                      <option value="DENIED">Deny</option>
                      <option value="RESOLVED">{selectedTicket.ticket_type === 'CONTRACTING' ? 'Resolve' : 'Resolve (Apply Changes)'}</option>
                    </select>
                  </div>

                  {['RESOLVED', 'APPROVED'].includes(actionStatus) && selectedTicket.requested_commission_level && (
                    <div className="space-y-1">
                      <Label>Final Commission Level (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={finalCommission}
                        onChange={(e) => setFinalCommission(e.target.value)}
                        placeholder={selectedTicket.requested_commission_level}
                      />
                    </div>
                  )}

                  {['DENIED', 'RESOLVED'].includes(actionStatus) && (
                    <div className="space-y-1">
                      <Label>Resolution Notes *</Label>
                      <Textarea
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        rows={4}
                        placeholder="Explain your decision..."
                      />
                    </div>
                  )}

                  <Button
                    onClick={handleAction}
                    disabled={!actionStatus || submitting}
                    className="w-full bg-gradient-to-r from-[#2447D6] to-blue-500"
                  >
                    {submitting ? 'Processing...' : 'Submit Action'}
                  </Button>
                </div>
              )}

              {/* Previous Resolution */}
              {selectedTicket.admin_resolution_notes && (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="text-xs text-[#A3AED0] uppercase mb-2">Resolution</div>
                  <div className="text-sm whitespace-pre-wrap mb-2">{selectedTicket.admin_resolution_notes}</div>
                  {selectedTicket.resolved_by_admin_name && (
                    <div className="text-xs text-[#A3AED0]">
                      By {selectedTicket.resolved_by_admin_name} on {formatDate(selectedTicket.resolved_at)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
